import { useState } from "react";
import { useSiteBrand, shortPlace, googleCalendarUrl } from "./brand";
import { CalendarIcon, MapPinIcon, MessageIcon, ArrowRightIcon } from "./icons";
import { GlassCard, SectionHeader } from "./ui";
import { parseLocalDate, fmtDate, fmtWeekday, fmtTime, serviceTypeLabel } from "./utils";

export function NextServicesSection({ services, whatsapp, loading, calendarUrl }) {
  const [view, setView] = useState("upcoming");
  const brand = useSiteBrand();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const upcoming = (services || []).filter((service) => {
    const date = parseLocalDate(service.date);
    return date && date >= todayStart;
  });
  const previous = (services || [])
    .filter((service) => {
      const date = parseLocalDate(service.date);
      return date && date < todayStart;
    })
    .sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date));
  const visibleServices = (view === "upcoming" ? upcoming : previous).slice(0, 6);

  return (
    <section id="cultos" className="public-section public-services-section py-10 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-7 lg:px-8 rounded-[2rem] border border-white dark:border-slate-900/80 bg-white dark:bg-slate-900/30 shadow-[0_18px_45px_rgba(79,70,229,0.08)]">
        <SectionHeader
          k="Agenda"
          title="Cultos e encontros"
          desc="Consulte os próximos encontros e reveja os cultos anteriores da nossa comunidade."
          right={
            whatsapp ? (
              <a
                href={whatsapp}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:inline-flex px-4 py-2 rounded-full border border-white dark:border-slate-900/70 bg-white dark:bg-slate-900/60 text-slate-800 dark:text-slate-200 font-semibold shadow-sm hover:bg-white dark:hover:bg-slate-900/75"
              >
                Falar no WhatsApp
              </a>
            ) : null
          }
        />

        {!loading && upcoming[0] ? (
          <div className="mb-5 flex flex-col gap-4 rounded-3xl bg-gradient-to-r from-slate-950 via-indigo-950 to-indigo-800 p-5 text-white shadow-[0_18px_36px_rgba(15,23,42,0.18)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white dark:bg-slate-900/15 text-indigo-100">
                <CalendarIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-200">Próximo encontro</div>
                <div className="mt-1 text-lg font-extrabold capitalize">{fmtWeekday(upcoming[0].date)} · {fmtDate(upcoming[0].date)}</div>
                <div className="mt-1 text-sm text-indigo-100">{fmtTime(upcoming[0].time) || "Horário a confirmar"} · {serviceTypeLabel(upcoming[0])} · {shortPlace(upcoming[0].place, brand)}</div>
              </div>
            </div>
            {calendarUrl || upcoming[0] ? (
              <a
                href={calendarUrl || googleCalendarUrl(upcoming[0], shortPlace(upcoming[0].place, brand), brand)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-950 dark:text-slate-50 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition"
              >
                Adicionar ao calendário <ArrowRightIcon />
              </a>
            ) : null}
          </div>
        ) : null}

        {!loading ? (
          <div className="mb-5 inline-flex rounded-2xl border border-white dark:border-slate-900/80 bg-white dark:bg-slate-900/65 p-1 shadow-sm" role="tablist" aria-label="Agenda de cultos">
            {[
              ["upcoming", `Próximos (${upcoming.length})`],
              ["previous", `Anteriores (${previous.length})`],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={view === key}
                onClick={() => setView(key)}
                className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition ${
                  view === key ? "bg-indigo-600 text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-900/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <div className="public-services-grid grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, idx) => (
              <GlassCard key={`svc-skeleton-${idx}`} className="p-5 animate-pulse">
                <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-700 mb-3" />
                <div className="h-5 w-2/3 rounded bg-slate-200 dark:bg-slate-700 mb-2" />
                <div className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700 mb-4" />
                <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700 mb-2" />
                <div className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
              </GlassCard>
            ))}
          </div>
        ) : visibleServices.length > 0 ? (
          <div className="public-services-grid grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleServices.map((s, index) => {
              const place = shortPlace(s.place, brand);
              const calHref = calendarUrl || googleCalendarUrl(s, place, brand);
              const isNext = view === "upcoming" && index === 0;
              return (
                <GlassCard
                  key={s.id}
                  className={`relative p-5 transition ${
                    isNext
                      ? "border-indigo-200 dark:border-indigo-800/50 bg-gradient-to-br from-white dark:from-slate-900 via-indigo-50/80 dark:via-indigo-950/40 to-sky-50/80 dark:to-sky-950/40 shadow-[0_16px_34px_rgba(79,70,229,0.12)]"
                      : "hover:shadow-md"
                  }`}
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold capitalize">
                        {fmtWeekday(s.date)}
                      </span>
                      {isNext ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2.5 py-1 text-[11px] font-bold text-emerald-800 dark:text-emerald-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                          Próximo culto
                        </span>
                      ) : null}
                    </div>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{fmtDate(s.date)}</span>
                  </div>

                  <div className="font-semibold text-slate-900 dark:text-slate-100 text-lg tracking-tight">{serviceTypeLabel(s)}</div>
                  {(s.theme || s.notes) && (
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{s.theme || s.notes}</div>
                  )}

                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <CalendarIcon /> <span className="font-medium">{fmtTime(s.time) || "-"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                      <MapPinIcon /> <span className="font-medium">{place}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-4">
                    {calHref ? (
                      <a
                        href={calHref}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:text-indigo-500 dark:hover:text-indigo-300"
                      >
                        <CalendarIcon /> {isNext ? "Adicionar ao calendário" : "Calendário"}
                      </a>
                    ) : null}
                    {whatsapp ? (
                      <a
                        href={whatsapp}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:text-indigo-500 dark:hover:text-indigo-300"
                      >
                        <MessageIcon /> WhatsApp
                      </a>
                    ) : null}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        ) : (
          <GlassCard className="p-8 text-center border-dashed border-indigo-200 dark:border-indigo-800/50">
            <div className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {view === "upcoming" ? "Ainda não há próximos cultos publicados." : "Ainda não há cultos anteriores na agenda."}
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {view === "upcoming" ? "Acompanhe os anúncios ou fale connosco para saber mais." : "Quando existirem registos anteriores, eles aparecerão aqui."}
            </p>
            {whatsapp ? (
              <a
                href={whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex px-4 py-2 rounded-full bg-indigo-600 text-white font-semibold shadow-sm hover:bg-indigo-500"
              >
                Falar no WhatsApp
              </a>
            ) : null}
          </GlassCard>
        )}
      </div>
    </section>
  );
}
